import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Loader2, PenLine } from "lucide-react";
import {
  resolveSplitParts,
  sliceValue,
  cleanForSlice,
  overlayFontSizeForFieldBox,
  fontSizeForSplitOverlayPart,
} from "@/lib/splitFieldCoords";

// A read-only, DocuSign-style PDF viewer: renders ALL pages of a form stacked
// in a single vertical-scroll container, with data fields pre-filled and
// signature fields shown as clickable "Sign here" tabs (manual placement).
//
// Coordinate handling mirrors PdfFieldPlacer's preview mode: fields carry a
// relative box (fieldCoordinates.unit === "relative") OR are matched to a PDF
// AcroForm widget annotation by field name. Font/box helpers are reused from
// splitFieldCoords so visuals match the existing preview.

type RelativeBox = { x: number; y: number; width: number; height: number };

export type SigningField = {
  id: number;
  fieldName?: string | null;
  fullName?: string | null;
  fieldLabel?: string | null;
  fieldType?: string | null;
  pageNumber?: number | null;
  fieldCoordinates?: any;
};

export interface SigningDocViewerHandle {
  /** Smooth-scroll the field into view within the document container. */
  scrollToField: (fieldId: number) => void;
}

interface SigningDocViewerProps {
  pdfUrl: string;
  fields: SigningField[];
  /** Pre-filled text/checkbox values keyed by field id (NO signature fields). */
  dataValueById: Record<number, string>;
  /** Signature field ids that have been signed (within this form). */
  signedFieldIds: Set<number>;
  /** Adopted signature image (data URL) applied when a signature tab is clicked. */
  signatureData: string;
  onSignField: (fieldId: number) => void;
  /** Fired when the PDF can't be loaded (missing/corrupt file). */
  onLoadError?: () => void;
  /** Fired once the PDF document loads successfully. */
  onLoaded?: () => void;
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  return Math.min(1, Math.max(0, v));
}

function coerceRelativeBox(coords: unknown): RelativeBox | null {
  const c = coords as any;
  if (!c || c.unit !== "relative") return null;
  if (
    typeof c.x !== "number" ||
    typeof c.y !== "number" ||
    typeof c.width !== "number" ||
    typeof c.height !== "number"
  ) {
    return null;
  }
  return {
    x: clamp01(c.x),
    y: clamp01(c.y),
    width: clamp01(c.width),
    height: clamp01(c.height),
  };
}

function fieldNameOf(f: SigningField): string {
  return String(f.fieldName || f.fullName || f.fieldLabel || "").trim();
}

function isSignatureField(f: SigningField): boolean {
  return String(f.fieldType ?? "").toLowerCase() === "signature";
}

type Placement = { field: SigningField; box: RelativeBox };

const MAX_PAGE_WIDTH = 880;

export const SigningDocViewer = forwardRef<
  SigningDocViewerHandle,
  SigningDocViewerProps
>(function SigningDocViewer(
  { pdfUrl, fields, dataValueById, signedFieldIds, signatureData, onSignField, onLoadError, onLoaded },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldElRefs = useRef<Record<number, HTMLElement | null>>({});
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [numPages, setNumPages] = useState(0);
  const [acroByPage, setAcroByPage] = useState<Record<number, Record<string, RelativeBox>>>({});
  const [targetWidth, setTargetWidth] = useState(MAX_PAGE_WIDTH);
  const [loadError, setLoadError] = useState(false);
  const onLoadedRef = useRef(onLoaded);
  const onLoadErrorRef = useRef(onLoadError);
  onLoadedRef.current = onLoaded;
  onLoadErrorRef.current = onLoadError;

  useImperativeHandle(ref, () => ({
    scrollToField: (fieldId: number) => {
      fieldElRefs.current[fieldId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    },
  }));

  // Measure container width so pages fit (capped).
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () =>
      setTargetWidth(Math.min(MAX_PAGE_WIDTH, Math.max(320, el.clientWidth - 32)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Load the PDF + extract AcroForm widget rects per page (for fields without
  // saved relative coordinates), mirroring PdfFieldPlacer.
  useEffect(() => {
    if (!pdfUrl) return;
    let cancelled = false;
    setLoadError(false);

    (async () => {
      let pdfjsLib: any;
      try {
        pdfjsLib = await import("pdfjs-dist");
      } catch {
        pdfjsLib = await import("pdfjs-dist/legacy/build/pdf.mjs");
      }
      let workerUrl: string;
      try {
        workerUrl = (await import("pdfjs-dist/build/pdf.worker.mjs?url")).default;
      } catch {
        workerUrl = (await import("pdfjs-dist/legacy/build/pdf.worker.mjs?url")).default;
      }
      pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

      const loaded = await pdfjsLib.getDocument({ url: pdfUrl, withCredentials: true }).promise;
      if (cancelled) return;
      setPdfDoc(loaded);
      setNumPages(loaded.numPages || 0);
      onLoadedRef.current?.();

      const acro: Record<number, Record<string, RelativeBox>> = {};
      for (let pageNumber = 1; pageNumber <= (loaded.numPages || 0); pageNumber++) {
        try {
          const page = await loaded.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 1 });
          const annots = await page.getAnnotations({ intent: "display" });
          const forPage: Record<string, RelativeBox> = {};
          for (const a of annots as any[]) {
            if (!a || a.subtype !== "Widget") continue;
            const name = String(a.fieldName || a.fullName || a.alternativeText || "").trim();
            const rect = a.rect as [number, number, number, number] | undefined;
            if (!name || !rect || rect.length !== 4) continue;
            const [vx1, vy1, vx2, vy2] = viewport.convertToViewportRectangle(rect);
            const left = Math.min(vx1, vx2);
            const top = Math.min(vy1, vy2);
            const w = Math.abs(vx2 - vx1);
            const h = Math.abs(vy2 - vy1);
            if (!w || !h) continue;
            forPage[name] = {
              x: clamp01(left / viewport.width),
              y: clamp01(top / viewport.height),
              width: clamp01(w / viewport.width),
              height: clamp01(h / viewport.height),
            };
          }
          acro[pageNumber] = forPage;
        } catch {
          acro[pageNumber] = {};
        }
      }
      if (!cancelled) setAcroByPage(acro);
    })().catch((err) => {
      console.error("[SigningDocViewer] Failed to load PDF", err);
      if (!cancelled) {
        setLoadError(true);
        onLoadErrorRef.current?.();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  // Resolve each field to a page + box (relative coords first, else acro by name).
  const placementsByPage = useMemo(() => {
    const byPage: Record<number, Placement[]> = {};
    for (const field of fields) {
      const rel = coerceRelativeBox(field.fieldCoordinates);
      if (rel && field.pageNumber) {
        (byPage[field.pageNumber] ||= []).push({ field, box: rel });
        continue;
      }
      const name = fieldNameOf(field);
      if (!name) continue;
      for (const pageStr of Object.keys(acroByPage)) {
        const pageNum = Number(pageStr);
        const box = acroByPage[pageNum]?.[name];
        if (box) {
          (byPage[pageNum] ||= []).push({ field, box });
          break;
        }
      }
    }
    return byPage;
  }, [fields, acroByPage]);

  if (loadError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-red-600">
        Couldn't load this form's document.
      </div>
    );
  }

  if (!pdfDoc) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-full overflow-y-auto bg-gray-100 px-4 py-4 [scrollbar-gutter:stable]"
    >
      <div className="mx-auto flex flex-col items-center gap-4">
        {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
          <SigningPage
            key={pageNumber}
            pdfDoc={pdfDoc}
            pageNumber={pageNumber}
            targetWidth={targetWidth}
            placements={placementsByPage[pageNumber] || []}
            dataValueById={dataValueById}
            signedFieldIds={signedFieldIds}
            signatureData={signatureData}
            onSignField={onSignField}
            registerFieldEl={(fieldId, el) => {
              fieldElRefs.current[fieldId] = el;
            }}
          />
        ))}
      </div>
    </div>
  );
});

interface SigningPageProps {
  pdfDoc: any;
  pageNumber: number;
  targetWidth: number;
  placements: Placement[];
  dataValueById: Record<number, string>;
  signedFieldIds: Set<number>;
  signatureData: string;
  onSignField: (fieldId: number) => void;
  registerFieldEl: (fieldId: number, el: HTMLElement | null) => void;
}

function SigningPage({
  pdfDoc,
  pageNumber,
  targetWidth,
  placements,
  dataValueById,
  signedFieldIds,
  signatureData,
  onSignField,
  registerFieldEl,
}: SigningPageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cssSize, setCssSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let cancelled = false;

    (async () => {
      const page = await pdfDoc.getPage(pageNumber);
      const base = page.getViewport({ scale: 1 });
      const dpr = Math.min(typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1, 2);
      const scale = (targetWidth * dpr) / base.width;
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current!;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const cssWidth = targetWidth;
      const cssHeight = canvas.height / dpr;
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      if (!cancelled) setCssSize({ width: cssWidth, height: cssHeight });

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport }).promise;
    })().catch((err) => {
      console.error(`[SigningDocViewer] Failed to render page ${pageNumber}`, err);
    });

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, pageNumber, targetWidth]);

  const vw = cssSize?.width ?? 0;
  const vh = cssSize?.height ?? 0;

  return (
    <div className="relative inline-block shadow-sm ring-1 ring-gray-300">
      <canvas ref={canvasRef} className="block" />
      <div
        className="absolute left-0 top-0"
        style={{ width: vw, height: vh }}
      >
        {cssSize &&
          placements.map(({ field, box }) => {
            const left = vw * box.x;
            const top = vh * box.y;
            const width = vw * box.width;
            const height = vh * box.height;
            const key = field.id;

            // Signature field: clickable tab (unsigned) or stamped image (signed).
            if (isSignatureField(field)) {
              const signed = signedFieldIds.has(field.id);
              return (
                <div
                  key={key}
                  ref={(el) => registerFieldEl(field.id, el)}
                  className="absolute"
                  style={{ left, top, width, height }}
                >
                  {signed ? (
                    <img
                      src={signatureData}
                      alt="Signature"
                      className="h-full w-full"
                      style={{ objectFit: "contain", filter: "contrast(1.2)" }}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => onSignField(field.id)}
                      className="flex h-full w-full items-center justify-center gap-1 rounded-sm border-2 border-amber-500 bg-amber-300/70 text-[11px] font-semibold text-amber-900 shadow-sm transition-colors hover:bg-amber-300"
                      title="Click to sign"
                    >
                      <PenLine className="h-3 w-3 shrink-0" />
                      <span className="truncate">Sign</span>
                    </button>
                  )}
                </div>
              );
            }

            // Data field (text / checkbox / split).
            const valueText = (dataValueById[field.id] ?? "").trim();
            const fieldType = String(field.fieldType ?? "text");
            const isCheckbox = fieldType === "checkbox";
            const splitParts = resolveSplitParts(field.fieldCoordinates);

            if (splitParts && (isCheckbox || valueText)) {
              return (
                <div
                  key={key}
                  ref={(el) => registerFieldEl(field.id, el)}
                  className="absolute"
                  style={{ left: 0, top: 0, width: vw, height: vh }}
                >
                  {splitParts.map((part, idx) => {
                    const pLeft = vw * part.x;
                    const pTop = vh * part.y;
                    const pWidth = vw * part.width;
                    const pHeight = vh * part.height;
                    const charText = isCheckbox
                      ? valueText
                      : sliceValue(valueText, part.start, part.length);
                    const fontSize = fontSizeForSplitOverlayPart({
                      parentWidthPx: width,
                      parentHeightPx: height,
                      fieldValueText: cleanForSlice(valueText),
                      sliceText: charText,
                    });
                    return (
                      <div
                        key={`${key}-${idx}`}
                        className="absolute flex items-center justify-center overflow-hidden text-gray-900"
                        style={{ left: pLeft, top: pTop, width: pWidth, height: pHeight, fontSize, lineHeight: 1.1 }}
                      >
                        {charText}
                      </div>
                    );
                  })}
                </div>
              );
            }

            const fontSize = overlayFontSizeForFieldBox({
              widthPx: width,
              heightPx: height,
              text: valueText || " ",
            });
            return (
              <div
                key={key}
                ref={(el) => registerFieldEl(field.id, el)}
                className="absolute flex items-center justify-center overflow-hidden pointer-events-none"
                style={{ left, top, width, height }}
              >
                <div
                  className={
                    (isCheckbox ? "w-full text-center " : "w-full truncate px-1 ") +
                    "text-gray-900"
                  }
                  style={{ fontSize, lineHeight: 1.1 }}
                >
                  {valueText}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
